import React from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Check, Tv, Zap, Shield, Clock } from "lucide-react";

const AbonnementIptv3Mois = () => {
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Abonnement IPTV 3 Mois - Roi du Streaming",
    "description": "Abonnement IPTV premium 3 mois avec code Xtream livré automatiquement. Accès à +15000 chaînes, VOD, sports HD/4K. Compatible Smart TV, Firestick, Android.",
    "image": ["https://roidustreaming.fr/favicon.png"],
    "brand": { "@type": "Brand", "name": "Roi du Streaming" },
    "offers": {
      "@type": "Offer",
      "name": "Abonnement IPTV 3 Mois",
      "price": "24.99",
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "url": "https://roidustreaming.fr/abonnement-iptv-3-mois",
      "priceValidUntil": "2026-12-31",
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "applicableCountry": "FR",
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 14,
        "returnMethod": "https://schema.org/ReturnByMail",
        "returnFees": "https://schema.org/FreeReturn"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": { "@type": "MonetaryAmount", "value": "0", "currency": "EUR" },
        "shippingDestination": { "@type": "DefinedRegion", "addressCountry": "FR" },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 0, "unitCode": "h" },
          "transitTime": { "@type": "QuantitativeValue", "minValue": 0, "maxValue": 1, "unitCode": "h" }
        }
      }
    },
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "1247", "bestRating": "5", "worstRating": "1" }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://roidustreaming.fr" },
      { "@type": "ListItem", "position": 2, "name": "Nos Offres", "item": "https://roidustreaming.fr/nos-offres" },
      { "@type": "ListItem", "position": 3, "name": "Abonnement IPTV 3 Mois", "item": "https://roidustreaming.fr/abonnement-iptv-3-mois" }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Abonnement IPTV 3 Mois – Xtream HD sans engagement | Roi du Streaming</title>
        <meta name="description" content="Activez votre IPTV pour 3 mois avec un code Xtream livré automatiquement. Compatible tous supports. Sans engagement. +15000 chaînes HD/4K." />
        <meta name="keywords" content="abonnement iptv 3 mois, iptv 3 mois xtream, iptv sans engagement, code xtream iptv, iptv premium 3 mois" />
        <link rel="canonical" href="https://roidustreaming.fr/abonnement-iptv-3-mois" />
        <meta property="og:title" content="Abonnement IPTV 3 Mois – Xtream HD sans engagement" />
        <meta property="og:description" content="Activez votre IPTV pour 3 mois avec un code Xtream livré automatiquement. Compatible tous supports." />
        <meta property="og:url" content="https://roidustreaming.fr/abonnement-iptv-3-mois" />
        <meta property="og:type" content="product" />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <Header />

      <main className="py-12 md:py-20 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumbs */}
          <nav className="text-sm text-muted-foreground mb-8" aria-label="Fil d'Ariane">
            <ol className="flex flex-wrap gap-2">
              <li><Link to="/" className="hover:text-primary transition-colors">Accueil</Link></li>
              <li>/</li>
              <li><Link to="/nos-offres" className="hover:text-primary transition-colors">Nos Offres</Link></li>
              <li>/</li>
              <li className="text-foreground">Abonnement 3 Mois</li>
            </ol>
          </nav>

          {/* Hero Section */}
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              Formule Découverte
            </span>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6">
              Abonnement IPTV 3 Mois – <span className="gradient-text">Accès instantané</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              L'abonnement 3 mois vous offre une solution simple et efficace pour accéder à tous vos contenus télé, sans contrainte.
            </p>
          </div>

          {/* Price Card */}
          <div className="card-premium p-8 rounded-2xl mb-12 text-center">
            <div className="text-5xl md:text-6xl font-bold text-primary mb-2">24,99€</div>
            <p className="text-muted-foreground mb-6">pour 3 mois d'accès complet</p>
            <Link to="/nos-offres">
              <Button className="btn-primary text-white font-semibold px-8 py-4 text-lg">
                Souscrire maintenant
              </Button>
            </Link>
          </div>

          {/* Content Section */}
          <div className="prose prose-lg prose-invert max-w-none mb-12">
            <p>
              Après votre achat, un code Xtream est généré et envoyé automatiquement. Vous pouvez l'utiliser 
              immédiatement dans l'application IPTV de votre choix.
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid sm:grid-cols-2 gap-6 mb-12">
            <div className="card-premium p-6 rounded-xl flex gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Tv className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">+15 000 chaînes</h3>
                <p className="text-sm text-muted-foreground">Chaînes internationales en direct</p>
              </div>
            </div>
            <div className="card-premium p-6 rounded-xl flex gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Qualité HD & 4K</h3>
                <p className="text-sm text-muted-foreground">Selon l'appareil utilisé</p>
              </div>
            </div>
            <div className="card-premium p-6 rounded-xl flex gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Films, séries & sports</h3>
                <p className="text-sm text-muted-foreground">VOD complète et événements en direct</p>
              </div>
            </div>
            <div className="card-premium p-6 rounded-xl flex gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Multi-appareils</h3>
                <p className="text-sm text-muted-foreground">Smart TV, Firestick, Android, PC/Mac</p>
              </div>
            </div>
          </div>

          {/* Benefits List */}
          <div className="card-premium p-8 rounded-2xl mb-12">
            <h2 className="text-2xl font-bold mb-6">Ce qui est inclus</h2>
            <ul className="space-y-4">
              {[
                "Accès à plus de 15 000 chaînes internationales",
                "Films, séries, documentaires et sports en direct",
                "Qualité HD et 4K selon l'appareil utilisé",
                "Fonctionne sur Smart TV, Android Box, Firestick, PC/Mac, tablette",
                "Code Xtream livré automatiquement par email",
                "Support client disponible via le site"
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA Section */}
          <div className="text-center">
            <p className="text-muted-foreground mb-6">
              En cas de question, notre équipe est disponible via le support intégré au site.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/nos-offres">
                <Button className="btn-primary text-white font-semibold px-8">
                  Voir toutes les offres
                </Button>
              </Link>
              <Link to="/faq">
                <Button variant="outline" className="px-8">
                  Questions fréquentes
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AbonnementIptv3Mois;
